import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useErrorsContext } from '../../../contexts/ErrorsContext';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { getTransactions } from '../../../services/transactionsService';
import { constants, DateUtils } from '../../../utils';
import { useParametrics } from '../../../contexts/ParametricsContext';
import Button from '../../shared/buttons/button/Button';
import Loading from '../../shared/loading/Loading';
import TransactionCard from '../card/TransactionCard';

const CREATOR_WALLET = 'CREATOR';

const TransactionList = ({ petitionId, wallet }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { convocationId } = useParametrics();
  const { showError } = useErrorsContext();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [filters] = useLocalStorage(constants.FILTERS, null);

  const generateFiltersObject = (p) => {
    const obj = { page: p };
    if (wallet && wallet !== CREATOR_WALLET) obj.wallet = wallet;
    if (filters?.fromDate) obj.dateIni = DateUtils.formatToServerDateTime(filters.fromDate, '00:00:00');
    if (filters?.toDate) obj.dateEnd = DateUtils.formatToServerDateTime(filters.toDate, '00:00:00');
    if (filters?.status) obj.status = filters.status;
    return obj;
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setPage(0);
      const response = await getTransactions(convocationId, petitionId, generateFiltersObject(0));
      setTransactions(response.content);
      setHasMore(!response.last);
    } catch (e) {
      setHasMore(false);
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      setPage(nextPage);
      const response = await getTransactions(convocationId, petitionId, generateFiltersObject(nextPage));
      setHasMore(!response.last);
      setTransactions((prev) => [...prev, ...response.content]);
    } catch (e) {
      showError(e);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!convocationId) return;
    fetchTransactions();
  }, [convocationId]); // eslint-disable-line

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <Loading />
      </div>
    );
  }

  return (
    <div className="WalletDetail__content__transactions">
      <div className="WalletDetail__content__transactions__head">
        <div style={{ width: '50px' }} />
        <h4>{t('Transactions.title')}</h4>
        <button
          className="button-only-icon p-0"
          onClick={() =>
            history.push({
              pathname: `/petitions/${petitionId}/transactions/${wallet}/filters`,
              state: { filters: true },
            })
          }
        >
          <img src="/misc/icons/filters.png" alt="filters" />
        </button>
      </div>
      <div className="WalletDetail__content__transactions__list">
        {!transactions || transactions.length === 0 ? (
          <div className="Transactions__content__list__empty">
            <img src="/misc/icons/transactions-empty.png" alt="icon" />
            <span>{t('Transactions.noData')}</span>
          </div>
        ) : (
          transactions.map((item) => (
            <TransactionCard key={item.id} item={item} wallet={wallet} />
          ))
        )}
      </div>
      {hasMore && (
        <div className="WalletDetail__content__transactions__button">
          <Button
            disabled={loadingMore}
            className="buttonPrimaryLarge"
            text={t('Transactions.loadMore')}
            action={loadMore}
            styles={{
              width: '200px',
              backgroundColor: '#34fdfe',
              color: '#381940',
              marginTop: '16px',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default TransactionList;
